/**
 * Historie der Teaser-Analysen: Tabelle auf dem Desktop, Karten auf dem Handy.
 */

import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import type { TeaserAnalysis, TeaserModelOption } from '../../types/teaser';
import { PAGE_SIZE } from '../../hooks/useTeaser';

interface Props {
  items: TeaserAnalysis[];
  total: number;
  page: number;
  onPageChange: (page: number) => void;
  models: TeaserModelOption[];
  onDownload: (id: number) => void;
  downloadingId: number | null;
}

const statusChip = (a: TeaserAnalysis) => {
  switch (a.status) {
    case 'pending':
      return <Chip size="small" label="Wartet" />;
    case 'running':
      return (
        <Chip
          size="small"
          color="info"
          label={a.progress != null ? `Läuft ${a.progress} %` : 'Läuft'}
        />
      );
    case 'completed':
      return <Chip size="small" color="success" label="Fertig" />;
    default:
      return <Chip size="small" color="error" label="Fehlgeschlagen" />;
  }
};

const formatCost = (a: TeaserAnalysis): React.ReactNode => {
  if (a.cost == null) return '–';
  let text: string;
  try {
    text = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: a.currency || 'EUR',
    }).format(a.cost);
  } catch {
    text = a.cost.toFixed(2);
  }
  if (a.cost_complete === false) {
    return (
      <Tooltip title="Kosten unvollständig erfasst">
        <span>{`≥ ${text}`}</span>
      </Tooltip>
    );
  }
  return text;
};

const formatScore = (v: number | null) => (v == null ? '–' : v.toFixed(1).replace('.', ','));
const formatRate = (v: number | null) =>
  v == null ? '–' : `${Math.round(v)} %`;

const TeaserHistory: React.FC<Props> = ({
  items,
  total,
  page,
  onPageChange,
  models,
  onDownload,
  downloadingId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const nameByCode = useMemo(() => {
    const map: Record<string, string> = {};
    models.forEach((m) => {
      map[m.model_code] = m.display_name;
    });
    return map;
  }, [models]);

  const modelChips = (a: TeaserAnalysis) => (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {a.models.map((code) => (
        <Chip key={code} size="small" variant="outlined" label={nameByCode[code] ?? code} />
      ))}
    </Stack>
  );

  const downloadButton = (a: TeaserAnalysis) => (
    <Tooltip title="CSV herunterladen">
      <span>
        <IconButton
          size="small"
          aria-label="CSV herunterladen"
          disabled={a.status === 'pending' || a.status === 'running' || downloadingId === a.id}
          onClick={() => onDownload(a.id)}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  if (items.length === 0 && total === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">Noch keine Analysen vorhanden.</Typography>
      </Paper>
    );
  }

  const pagination = (
    <TablePagination
      component="div"
      count={total}
      page={page}
      rowsPerPage={PAGE_SIZE}
      rowsPerPageOptions={[PAGE_SIZE]}
      onPageChange={(_, p) => onPageChange(p)}
      labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
    />
  );

  if (isMobile) {
    return (
      <Box>
        <Stack spacing={1.5}>
          {items.map((a) => (
            <Card key={a.id} variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {a.utility_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(a.created_at).toLocaleString('de-DE')}
                      {a.created_by ? ` · ${a.created_by}` : ''}
                    </Typography>
                  </Box>
                  {statusChip(a)}
                </Stack>
                <Box sx={{ my: 1 }}>{modelChips(a)}</Box>
                <Typography variant="body2">
                  Fragen: {a.default_prompt_count} + {a.custom_prompt_count}
                </Typography>
                <Typography variant="body2">
                  LLM Score: {formatScore(a.average_score)} · Nennungsrate:{' '}
                  {formatRate(a.mention_rate)}
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Kosten: {formatCost(a)}</Typography>
                  {downloadButton(a)}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
        {pagination}
      </Box>
    );
  }

  return (
    <Paper>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Datum</TableCell>
              <TableCell>Stadtwerk</TableCell>
              <TableCell>Modelle</TableCell>
              <TableCell>Fragen</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">LLM Score</TableCell>
              <TableCell align="right">Nennungsrate</TableCell>
              <TableCell align="right">Kosten</TableCell>
              <TableCell>Ersteller</TableCell>
              <TableCell align="center">CSV</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((a) => (
              <TableRow key={a.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {new Date(a.created_at).toLocaleString('de-DE')}
                </TableCell>
                <TableCell>{a.utility_name}</TableCell>
                <TableCell>{modelChips(a)}</TableCell>
                <TableCell>{`${a.default_prompt_count} + ${a.custom_prompt_count}`}</TableCell>
                <TableCell>{statusChip(a)}</TableCell>
                <TableCell align="right">{formatScore(a.average_score)}</TableCell>
                <TableCell align="right">{formatRate(a.mention_rate)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {formatCost(a)}
                </TableCell>
                <TableCell>{a.created_by ?? '–'}</TableCell>
                <TableCell align="center">{downloadButton(a)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination}
    </Paper>
  );
};

export default TeaserHistory;
